import { TrackingEventName, track } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { trackPricingPlansModalTeamUpgradeRequestSubmit } from '@heidi/commerce/data';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyCaption, TypographyH2 } from '@heidi/design-system/typography';
import { SubscriptionTier, type BillingSeatType } from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { openTeamSeatUpgradeRequestModalAtom } from '../../../../trials-and-upgrades/team-seat-upgrade-request-modal/atoms';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';
import {
  pricingPlansHighlightedPlanAtom,
  pricingPlansModalOpenAtom,
} from '../../billingAtoms';
import { useIsTeamSeatUpgradeRequester } from '../../hooks/useIsTeamSeatUpgradeRequester';
import { usePricingPlansUpgradeRole } from '../../hooks/usePricingPlansUpgradeRole';
import { PracticeSeatUpgradePlans } from './practice-seat-upgrade-plans';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';
import { PricingPlansStatusBanner } from './pricing-plans-status-banner';
import { PricingPlanCards } from './PricingPlanCards';
import { trialEndPricingPlanCardsMessages } from './trial-end-pricing-plan-cards.messages';
import { usePricingPlansModalState } from './usePricingPlansModalState';

/** Who is looking, written by the body so the shell's events carry it. */
type PricingPlansAnalyticsContext = {
  currentPlan?: SubscriptionTier;
  currentSeatType?: BillingSeatType;
  isAdmin?: boolean;
};

export const PricingPlansModal = () => {
  const [isOpen, setIsOpen] = useAtom(pricingPlansModalOpenAtom);
  const setHighlightedPlan = useSetAtom(pricingPlansHighlightedPlanAtom);
  const wasOpenRef = useRef(false);
  const analyticsContextRef = useRef<PricingPlansAnalyticsContext>({});

  // Radix and vaul call this only for a user dismissal; a CTA that closes the
  // modal writes the atom directly, so it is never counted as a close.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        const { currentPlan, currentSeatType } = analyticsContextRef.current;
        track({
          options: { amplitude: false },
          event_name: TrackingEventName.ButtonClicked,
          props: {
            screen_name: ScreenName.PricingPlansModal,
            action: ButtonAction.PricingPlansModalClose,
            label: 'close',
            ...(currentPlan && { current_plan: currentPlan }),
            ...(currentSeatType && { current_seat_type: currentSeatType }),
          },
        });
      }
      setIsOpen(open);
    },
    [setIsOpen]
  );

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) setHighlightedPlan(null);
    wasOpenRef.current = isOpen;
  }, [isOpen, setHighlightedPlan]);

  // A Scribe Team member cannot buy here, so every entry point that opens this
  // modal hands them to the seat request modal instead.
  const isTeamSeatUpgradeRequester = useIsTeamSeatUpgradeRequester();
  const openTeamSeatUpgradeRequestModal = useSetAtom(openTeamSeatUpgradeRequestModalAtom);
  const shouldRedirectToSeatRequest = isOpen && isTeamSeatUpgradeRequester;
  useEffect(() => {
    if (!shouldRedirectToSeatRequest) return;
    setIsOpen(false);
    openTeamSeatUpgradeRequestModal({
      trackRequestSubmitted: trackPricingPlansModalTeamUpgradeRequestSubmit,
    });
  }, [openTeamSeatUpgradeRequestModal, setIsOpen, shouldRedirectToSeatRequest]);

  return (
    <DialogDrawer
      open={isOpen && !isTeamSeatUpgradeRequester}
      onOpenChange={handleOpenChange}
    >
      {/*
        880 wide with 48/32 padding is the design frame. The height stays
        content-driven — pinning it to the design's 703 would depend on every
        design-system control matching its Figma instance to the pixel, and the
        failure mode is clipped content. `PricingPlanCards` freezes the card row
        at its measured height instead, so the dialog never grows on expand.
        A single-card body (Practice seat checkout, Scribe Team / Clinician Max
        upgrade) narrows it to the 540 design frame through `:has`, since the
        plan state is only read once the content mounts.
      */}
      <DialogDrawerContent className="flex max-h-[90dvh] min-h-0 w-full flex-col overflow-hidden bg-surface-elevated px-8 pb-8 pt-12 sm:max-w-[880px] sm:has-[[data-single-plan]]:max-w-[540px]">
        <PricingPlansModalBody analyticsContextRef={analyticsContextRef} />
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

function PricingPlansModalBody({
  analyticsContextRef,
}: {
  analyticsContextRef: MutableRefObject<PricingPlansAnalyticsContext>;
}) {
  const modalState = usePricingPlansModalState();
  const {
    isPracticeSeatCheckoutOffered,
    isScribeTeamUpgradeOffered,
    isClinicianMaxSeatUpgradeOffered,
    isClinicianMaxSeat,
    currentPlan,
    currentSeatType,
    isTrialing,
  } = modalState;
  const { isWorkspaceAdmin, isWorkspaceRoleUnresolved } = usePricingPlansUpgradeRole();
  const isAdmin = isWorkspaceAdmin;
  useEffect(() => {
    analyticsContextRef.current = {
      ...(currentPlan && { currentPlan }),
      ...(currentSeatType && { currentSeatType }),
      ...(isAdmin !== undefined && { isAdmin }),
    };
  }, [analyticsContextRef, currentPlan, currentSeatType, isAdmin]);

  // The body mounts once per open. The view waits for the plan and a settled
  // workspace role so it is never counted without them.
  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (hasTrackedViewRef.current || !currentPlan || isWorkspaceRoleUnresolved) return;
    hasTrackedViewRef.current = true;
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: {
        screen_name: ScreenName.PricingPlansModal,
        current_plan: currentPlan,
        ...(isAdmin !== undefined && { is_admin: isAdmin }),
      },
    });
  }, [currentPlan, isWorkspaceRoleUnresolved, isAdmin]);

  // Team upgrades share the one Practice card into the seat checkout when the
  // trial-end dual-seat layout is off. With trial-end on, Practice, Scribe Team,
  // and Clinician Max use that layout's Practice-only card (rising-tide seat
  // page + complimentary banner) instead.
  const isTrialEndEnabled = getCommerceBillingUiPorts().useIsHeidi2TrialEndEnabled();
  const seatCheckoutFromPlan =
    !isTrialEndEnabled && isPracticeSeatCheckoutOffered
      ? 'practice'
      : !isTrialEndEnabled && isScribeTeamUpgradeOffered
        ? 'scribeTeam'
        : !isTrialEndEnabled && isClinicianMaxSeatUpgradeOffered
          ? 'clinicianMax'
          : null;
  const isTrialEndMultiCard = seatCheckoutFromPlan === null && isTrialEndEnabled;
  const isTrialEndTeamPlan =
    isTrialEndMultiCard &&
    (isClinicianMaxSeat ||
      currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS ||
      currentPlan === SubscriptionTier.PRO_PLUS);

  const headingMessage =
    isPracticeSeatCheckoutOffered ||
    isScribeTeamUpgradeOffered ||
    (isTrialEndTeamPlan && !isClinicianMaxSeat)
      ? pricingPlansModalMessages.teamUpgradeTitle
      : isClinicianMaxSeatUpgradeOffered || (isTrialEndTeamPlan && isClinicianMaxSeat)
        ? pricingPlansModalMessages.clinicianMaxUpgradeTitle
        : isTrialEndMultiCard
          ? trialEndPricingPlanCardsMessages.title
          : pricingPlansModalMessages.title;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto overflow-x-hidden pb-10 sm:pb-0"
      data-single-plan={seatCheckoutFromPlan !== null || isTrialEndTeamPlan || undefined}
    >
      <div className="flex flex-col items-center gap-1 text-center">
        <TypographyH2 className="text-text-primary">
          <FormattedMessage {...headingMessage} />
        </TypographyH2>
        {isTrialEndMultiCard && !isTrialEndTeamPlan ? (
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage {...trialEndPricingPlanCardsMessages.cancelAnytime} />
          </TypographyCaption>
        ) : null}
      </div>

      {seatCheckoutFromPlan !== null ? (
        <div className="flex flex-col gap-4">
          <PricingPlansStatusBanner />
          <PracticeSeatUpgradePlans
            currentPlan={currentPlan}
            currentSeatType={currentSeatType}
            fromPlan={seatCheckoutFromPlan}
          />
        </div>
      ) : (
        <>
          {/*
            Trial-end complimentary offer owns the banner when not trialing.
            Free trial still uses the days-left status strip.
          */}
          {isTrialEndMultiCard && !isTrialing ? null : <PricingPlansStatusBanner />}
          <PricingPlanCards modalState={modalState} />
        </>
      )}
    </div>
  );
}
